import type {
  EmployeeStampProgress,
  LearningResource,
  PassportPageSummary,
  Stamp,
} from "@peer-start/platform-contracts";
import { Drawer } from "@peer-start/ui";
import { ChevronRight } from "lucide-react";
import { useTranslation } from "react-i18next";
import {
  buildCompetencySkillItems,
  LearningRouteCompetencySkillsList,
} from "./LearningRouteCompetencySkills.js";

function focusStampId(items: ReturnType<typeof buildCompetencySkillItems>): string | undefined {
  return items.find((item) => item.progress?.status !== "established")?.stamp.id ?? items[0]?.stamp.id;
}

export function LearningRouteCompetencyDrawer({
  pageId,
  pages,
  stamps,
  progress,
  resources,
  currentStampId,
  onClose,
  onSelectSkill,
  controlIdPrefix = "learning-route.full",
}: {
  pageId: string | null;
  pages: PassportPageSummary[];
  stamps: Stamp[];
  progress: EmployeeStampProgress[];
  resources: LearningResource[];
  currentStampId?: string;
  onClose: () => void;
  onSelectSkill?: (pageId: string, stampId: string) => void;
  controlIdPrefix?: string;
}) {
  const { t } = useTranslation();
  const openSummary = pages.find((entry) => entry.page.id === pageId) ?? null;
  const skillItems = openSummary
    ? buildCompetencySkillItems({
        summary: openSummary,
        stamps,
        progress,
        resources,
        currentStampId,
      })
    : [];
  const competencyFocusId = skillItems.find((item) => item.current)?.stamp.id ?? focusStampId(skillItems);
  const jumpToSkill = (stampId: string) => {
    if (!openSummary || !onSelectSkill) return;
    onSelectSkill(openSummary.page.id, stampId);
    onClose();
  };

  return (
    <Drawer
      bodyClassName="flex flex-col overflow-hidden p-0"
      closeLabel={t("common.close")}
      controlId="learning-route.competency-drawer"
      description={
        openSummary ? t("learningRoute.skillCount", { count: skillItems.length }) : undefined
      }
      onClose={onClose}
      open={Boolean(openSummary)}
      side="right"
      size="default"
      title={openSummary?.page.title ?? t("learningRoute.skills")}
    >
      {openSummary ? (
        <div className="flex h-full min-h-0 flex-col">
          <div className="min-h-0 flex-1 overflow-y-auto px-5 py-5 sm:px-6">
            <h3 className="mb-4 text-sm font-medium">{t("learningRoute.skills")}</h3>
            <LearningRouteCompetencySkillsList
              controlIdPrefix={`${controlIdPrefix}.skill`}
              items={skillItems}
              onSelectStamp={onSelectSkill ? jumpToSkill : undefined}
              showFocusHint
            />
          </div>
          {onSelectSkill && competencyFocusId ? (
            <div className="shrink-0 border-t border-border/70 px-5 py-4 sm:px-6">
              <button
                className="inline-flex w-full items-center justify-center gap-2 rounded-xl bg-primary px-4 py-3.5 text-sm font-medium text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline-none focus-visible:ring-3 focus-visible:ring-ring/30"
                data-control-id={`${controlIdPrefix}.focus.${openSummary.page.id}`}
                onClick={() => jumpToSkill(competencyFocusId)}
                type="button"
              >
                {t("learningRoute.focusCompetency")}
                <ChevronRight aria-hidden="true" className="size-4" />
              </button>
            </div>
          ) : null}
        </div>
      ) : null}
    </Drawer>
  );
}
